/**
 * Raw CPU and GPU benchmarks for performance tier (scoring done on backend).
 *
 * CPU: runs in a Web Worker (falls back to main thread).
 * Uses a WebAssembly kernel when available for deterministic compilation
 * (no JIT tiering/deoptimisation), with a JS fallback.
 * Window-based calibration during warm-up tail handles coarse timers
 * (Firefox 1 ms without COOP/COEP, Safari ~100 µs); a per-test
 * `performance.now()` resolution probe further adapts the per-run
 * duration so quantization error stays below ~3 % even on coarse-timer
 * browsers (fewer, longer runs on Firefox without isolation).
 * Calibration is run 3 times and the median is used to remove a single
 * GC/preemption spike from biasing every measurement run.
 * Measurement runs use pre-allocated Float64Array — zero allocations
 * in the hot path eliminates GC spikes.
 *
 * GPU: compute-heavy fragment shader (20-iteration sin/fract chain) on a
 * 512×512 canvas. When `EXT_disjoint_timer_query_webgl2` is available
 * (most Chromium browsers + Firefox with the extension enabled),
 * per-run timing uses GPU-side nanosecond queries that avoid the
 * `readPixels()` CPU↔GPU sync overhead and let us detect/discard
 * disjoint events (context switches, GPU preemption). Otherwise falls
 * back to the original `readPixels(1,1)` synchronous timing.
 * Same 3-shot-calibration → fixed-iteration measurement pattern as CPU.
 * Canvas2D fallback when WebGL/highp unavailable.
 */

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const CPU_BUDGET_MS = 300;
const CPU_WARMUP_MS = 80;
const CPU_CALIBRATION_MS = 20;
const CPU_CALIBRATION_SHOTS = 3;
const CPU_CAL_WINDOW_MS = 12;
const CPU_RUNS_FINE = 11;          // timer resolution < CPU_COARSE_TIMER_MS
const CPU_RUNS_COARSE = 7;         // Firefox without COOP/COEP (1 ms timer)
const CPU_COARSE_TIMER_MS = 0.5;
const CPU_TRIM_PER_SIDE = 1;       // trimmedMean: drop top & bottom sample
const CPU_WORKER_TIMEOUT_MS = CPU_BUDGET_MS + 2000;

const GPU_BUDGET_MS = 300;
const GPU_WARMUP_MS = 60;
const GPU_CALIBRATION_MS = 15;
const GPU_CALIBRATION_SHOTS = 3;
const GPU_CAL_WINDOW_MS = 10;
const GPU_RUNS = 12;
const GPU_TRIM_PER_SIDE = 1;
const GPU_QUERY_POLL_TIMEOUT_MS = 500;
const GPU_DISJOINT_MAX_RETRIES = 2;
const GPU_CANVAS_SIZE = 512;
const GPU_SHADER_ITERS = 20;

// ---------------------------------------------------------------------------
// STATE
// ---------------------------------------------------------------------------

let _glContext = null;
let _glContextAttempted = false;

// ---------------------------------------------------------------------------
// STATS HELPERS
//
// Defined at module scope and duplicated as a string for the worker so the
// worker has no dependency on the main bundle. Keep the two in sync.
// ---------------------------------------------------------------------------

function _sortedAsc(arr) {
  const a = new Array(arr.length);
  for (let i = 0; i < arr.length; i++) a[i] = arr[i];
  a.sort(function (x, y) { return x - y; });
  return a;
}
function statsMedian(arr) {
  if (!arr || arr.length === 0) return 0;
  const a = _sortedAsc(arr);
  const m = a.length >> 1;
  return (a.length & 1) ? a[m] : (a[m - 1] + a[m]) / 2;
}
function statsMean(arr) {
  if (!arr || arr.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < arr.length; i++) s += arr[i];
  return s / arr.length;
}
function statsTrimmedMean(arr, k) {
  if (!arr || arr.length === 0) return 0;
  if (arr.length <= 2 * k) return statsMedian(arr);
  const a = _sortedAsc(arr);
  let sum = 0;
  for (let i = k; i < a.length - k; i++) sum += a[i];
  return sum / (a.length - 2 * k);
}
function statsMad(arr) {
  if (!arr || arr.length === 0) return 0;
  const m = statsMedian(arr);
  const dev = new Array(arr.length);
  for (let i = 0; i < arr.length; i++) dev[i] = Math.abs(arr[i] - m);
  return statsMedian(dev);
}
function statsStddev(arr, mean) {
  if (!arr || arr.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < arr.length; i++) {
    const d = arr[i] - mean;
    s += d * d;
  }
  return Math.sqrt(s / arr.length);
}
function probeTimerResolutionMs() {
  let prev = performance.now();
  let res = Infinity;
  for (let i = 0; i < 10000; i++) {
    const now = performance.now();
    if (now > prev) {
      const d = now - prev;
      if (d < res) res = d;
      prev = now;
      if (res < 0.002) break;
    }
  }
  return isFinite(res) ? res : 1.0;
}

// Same helpers as a string, injected verbatim into the worker.
const STATS_HELPERS_SOURCE = `
function _sortedAsc(arr) {
  var a = new Array(arr.length);
  for (var i = 0; i < arr.length; i++) a[i] = arr[i];
  a.sort(function (x, y) { return x - y; });
  return a;
}
function statsMedian(arr) {
  if (!arr || arr.length === 0) return 0;
  var a = _sortedAsc(arr);
  var m = a.length >> 1;
  return (a.length & 1) ? a[m] : (a[m - 1] + a[m]) / 2;
}
function statsMean(arr) {
  if (!arr || arr.length === 0) return 0;
  var s = 0;
  for (var i = 0; i < arr.length; i++) s += arr[i];
  return s / arr.length;
}
function statsTrimmedMean(arr, k) {
  if (!arr || arr.length === 0) return 0;
  if (arr.length <= 2 * k) return statsMedian(arr);
  var a = _sortedAsc(arr);
  var sum = 0;
  for (var i = k; i < a.length - k; i++) sum += a[i];
  return sum / (a.length - 2 * k);
}
function statsMad(arr) {
  if (!arr || arr.length === 0) return 0;
  var m = statsMedian(arr);
  var dev = new Array(arr.length);
  for (var i = 0; i < arr.length; i++) dev[i] = Math.abs(arr[i] - m);
  return statsMedian(dev);
}
function statsStddev(arr, mean) {
  if (!arr || arr.length === 0) return 0;
  var s = 0;
  for (var i = 0; i < arr.length; i++) {
    var d = arr[i] - mean;
    s += d * d;
  }
  return Math.sqrt(s / arr.length);
}
function probeTimerResolutionMs() {
  var prev = performance.now();
  var res = Infinity;
  for (var i = 0; i < 10000; i++) {
    var now = performance.now();
    if (now > prev) {
      var d = now - prev;
      if (d < res) res = d;
      prev = now;
      if (res < 0.002) break;
    }
  }
  return isFinite(res) ? res : 1.0;
}
`;

// ---------------------------------------------------------------------------
// WEBGL CONTEXT
// ---------------------------------------------------------------------------

function getGLContext() {
  if (_glContextAttempted) return _glContext;
  _glContextAttempted = true;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = GPU_CANVAS_SIZE;
    const attrs = {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "high-performance",
      desynchronized: true,
    };
    _glContext =
      canvas.getContext("webgl2", attrs) ||
      canvas.getContext("webgl", attrs) ||
      canvas.getContext("experimental-webgl", attrs);
  } catch (e) {
    _glContext = null;
  }
  return _glContext;
}

function isWebGL2Context(gl) {
  return (
    typeof WebGL2RenderingContext !== "undefined" &&
    gl instanceof WebGL2RenderingContext
  );
}

function getGpuTimerQueryExt(gl) {
  if (!gl || !isWebGL2Context(gl)) return null;
  try {
    const ext = gl.getExtension("EXT_disjoint_timer_query_webgl2");
    if (!ext) return null;
    if (
      typeof ext.TIME_ELAPSED_EXT !== "number" ||
      typeof ext.GPU_DISJOINT_EXT !== "number"
    ) {
      return null;
    }
    return ext;
  } catch (e) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// WASM KERNEL BUILDER
//
// Constructs a tiny WebAssembly module exporting cpuKernel(n: i32): i32.
// Identical MurmurHash3-style mixer as the JS fallback.
// WASM eliminates JIT tiering variability — same binary always compiles
// to the same native code on a given engine.
// ---------------------------------------------------------------------------

function buildCpuWasm() {
  function lebU(v) {
    var r = [];
    do {
      var b = v & 0x7f;
      v >>>= 7;
      if (v) b |= 0x80;
      r.push(b);
    } while (v);
    return r;
  }
  function lebS(v) {
    var r = [];
    while (true) {
      var b = v & 0x7f;
      v >>= 7;
      if ((v === 0 && !(b & 0x40)) || (v === -1 && b & 0x40)) {
        r.push(b);
        break;
      }
      r.push(b | 0x80);
    }
    return r;
  }
  function sec(id, data) {
    return [id].concat(lebU(data.length)).concat(data);
  }

  var ts = sec(1, [1, 0x60, 1, 0x7f, 1, 0x7f]);
  var fs = sec(3, [1, 0]);
  var nm = [9, 99, 112, 117, 75, 101, 114, 110, 101, 108];
  var es = sec(7, [1].concat(nm).concat([0, 0]));

  var c = [1, 3, 0x7f];
  function p() {
    for (var i = 0; i < arguments.length; i++) c.push(arguments[i]);
  }
  function pv(a) {
    for (var i = 0; i < a.length; i++) c.push(a[i]);
  }

  p(0x41); pv(lebS(0x12345678)); p(0x21, 0x01);
  p(0x41); pv(lebS(0x87654321 | 0)); p(0x21, 0x02);
  p(0x41, 0x00, 0x21, 0x03);
  p(0x02, 0x40, 0x03, 0x40);
  p(0x20, 0x03, 0x20, 0x00, 0x4f, 0x0d, 0x01);
  p(0x20, 0x01, 0x20, 0x01);
  p(0x41); pv(lebS(16)); p(0x76, 0x73);
  p(0x41); pv(lebS(0x85ebca6b | 0)); p(0x6c, 0x21, 0x01);
  p(0x20, 0x02, 0x20, 0x02);
  p(0x41); pv(lebS(13)); p(0x76, 0x73);
  p(0x41); pv(lebS(0xc2b2ae35 | 0)); p(0x6c, 0x21, 0x02);
  p(0x20, 0x01, 0x20, 0x02, 0x6a, 0x21, 0x01);
  p(0x20, 0x02, 0x20, 0x01, 0x73, 0x21, 0x02);
  p(0x20, 0x03, 0x41, 0x01, 0x6a, 0x21, 0x03);
  p(0x0c, 0x00, 0x0b, 0x0b);
  p(0x20, 0x01, 0x20, 0x02, 0x73, 0x0b);

  var cs = sec(0x0a, [1].concat(lebU(c.length)).concat(c));
  return new Uint8Array(
    [0, 0x61, 0x73, 0x6d, 1, 0, 0, 0]
      .concat(ts)
      .concat(fs)
      .concat(es)
      .concat(cs)
  );
}

let _cpuWasmBytes = null;

function getCpuWasmBytes() {
  if (_cpuWasmBytes) return _cpuWasmBytes;
  try {
    _cpuWasmBytes = buildCpuWasm();
  } catch (e) {
    _cpuWasmBytes = null;
  }
  return _cpuWasmBytes;
}

// ---------------------------------------------------------------------------
// JS FALLBACK KERNEL (used when WASM unavailable)
// ---------------------------------------------------------------------------

function cpuKernelJS(iterations) {
  let a = 0x12345678 | 0;
  let b = 0x87654321 | 0;
  for (let i = 0; i < iterations; i++) {
    a = (Math.imul(a ^ (a >>> 16), 0x85ebca6b)) | 0;
    b = (Math.imul(b ^ (b >>> 13), 0xc2b2ae35)) | 0;
    a = (a + b) | 0;
    b = (b ^ a) | 0;
  }
  return (a ^ b) | 0;
}

const WORKER_HEV_KEYS = [
  'architecture',
  'bitness',
  'formFactors',
  'fullVersionList',
  'model',
  'platformVersion',
  'uaFullVersion',
  'wow64',
];

const WORKER_NAV_SOURCE = `
var hevKeys = ${JSON.stringify(WORKER_HEV_KEYS)};
function readWorkerNav() {
  var nav = { userAgent: null, appVersion: null, platform: null, highEntropyValues: null };
  try {
    nav.userAgent = navigator.userAgent;
    nav.appVersion = navigator.appVersion;
    nav.platform = navigator.platform;
  } catch (x) {}
  return nav;
}
`;

// ---------------------------------------------------------------------------
// CPU WORKER SOURCE
//
// Receives optional WASM bytes via postMessage. Compiles and verifies
// against the JS kernel; falls back to JS if anything fails.
//
// Warm-up uses a window-based throughput estimation (last `calWindowMs`
// of warm-up) so coarse timers (Firefox 1 ms) can still produce an
// accurate rough ops/ms for sizing the calibration call.
//
// Calibration runs `CPU_CALIBRATION_SHOTS` times; median elapsed is used
// to estimate ops/ms (removes a single GC/preemption spike from biasing
// the per-run target).
//
// Number of measurement runs is chosen *after* probing the timer:
// fine-resolution browsers get more, shorter runs (better variance
// information); coarse-resolution Firefox gets fewer, longer runs (so
// that timer-quantization error per sample stays below ~3 %).
//
// Measurement stores durations in a pre-allocated Float64Array —
// zero object allocations during the hot path.
// ---------------------------------------------------------------------------

const CPU_WORKER_SOURCE = `"use strict";
${STATS_HELPERS_SOURCE}
${WORKER_NAV_SOURCE}
function cpuKernelJS(n) {
  var a = 0x12345678 | 0, b = 0x87654321 | 0;
  for (var i = 0; i < n; i++) {
    a = Math.imul(a ^ (a >>> 16), 0x85ebca6b) | 0;
    b = Math.imul(b ^ (b >>> 13), 0xc2b2ae35) | 0;
    a = (a + b) | 0;
    b = (b ^ a) | 0;
  }
  return (a ^ b) | 0;
}
self.onmessage = function (e) {
  var c = e.data;
  var webWorkerNavStart = performance.now();
  var webWorkerNav = readWorkerNav();
  var webWorkerMs = 0;

  var postNav = function () {
    try {
      self.postMessage({
        kind: "nav",
        webWorker: webWorkerNav,
        webWorkerMs: performance.now() - webWorkerNavStart,
      });
    } catch (x) {}
  };
  postNav();

  var runCpu = function () {
  var cpuKernel = cpuKernelJS, isWasm = false;
  if (c.wasmBytes && typeof WebAssembly !== "undefined") {
    try {
      var m = new WebAssembly.Module(new Uint8Array(c.wasmBytes));
      var k = new WebAssembly.Instance(m).exports.cpuKernel;
      if (k(1) === cpuKernelJS(1)) { cpuKernel = k; isWasm = true; }
    } catch (x) {}
  }

  var timerResolutionMs = probeTimerResolutionMs();
  var useCoarseShape = timerResolutionMs >= ${CPU_COARSE_TIMER_MS};
  var numRuns = useCoarseShape ? ${CPU_RUNS_COARSE} : ${CPU_RUNS_FINE};
  var targetRunMs = c.measureBudgetMs / numRuns;

  var sink = 0, t0 = performance.now();
  var wEnd = t0 + c.warmupMs;
  var batch = 1000, calls = 0;
  var calWinStart = 0, calWinOps = 0, totalOps = 0;
  while (true) {
    var now = performance.now();
    if (now >= wEnd) break;
    if (calWinStart === 0 && now >= wEnd - c.calWindowMs) calWinStart = now;
    sink = (sink + cpuKernel(batch)) | 0;
    totalOps += batch;
    if (calWinStart > 0) calWinOps += batch;
    calls++;
    if (calls === 10) batch = 5000;
    if (calls === 30) batch = 10000;
    if (calls === 100) batch = 50000;
  }
  var warmupActual = performance.now() - t0;
  var calWinElapsed = calWinStart > 0 ? performance.now() - calWinStart : 0;
  var roughOpsPerMs;
  if (calWinElapsed > 0) roughOpsPerMs = calWinOps / calWinElapsed;
  else if (warmupActual > 0) roughOpsPerMs = totalOps / warmupActual;
  else roughOpsPerMs = 100000;

  var calShots = ${CPU_CALIBRATION_SHOTS};
  var calShotMs = c.calibrationMs / calShots;
  var calIters = Math.max(10000, Math.round(roughOpsPerMs * calShotMs));
  var calSamples = new Float64Array(calShots);
  for (var s = 0; s < calShots; s++) {
    var cs = performance.now();
    sink = (sink + cpuKernel(calIters)) | 0;
    calSamples[s] = performance.now() - cs;
  }
  var calElapsed = statsMedian(calSamples);
  var opsPerMs = calElapsed > 0 ? calIters / calElapsed : roughOpsPerMs;
  if (!isFinite(opsPerMs) || opsPerMs <= 0) opsPerMs = roughOpsPerMs;

  var targetIters = Math.round(opsPerMs * targetRunMs);
  if (!isFinite(targetIters) || targetIters < 1000) targetIters = 1000;
  var durations = new Float64Array(numRuns);
  for (var i = 0; i < numRuns; i++) {
    var rs = performance.now();
    var rv = cpuKernel(targetIters);
    var re = performance.now();
    sink = (sink + rv) | 0;
    durations[i] = re - rs;
  }
  var total = performance.now() - t0;

  var dArr = new Array(numRuns);
  for (var di = 0; di < numRuns; di++) dArr[di] = durations[di];
  var meanV = statsMean(dArr);
  var medianV = statsMedian(dArr);
  var trimmedV = statsTrimmedMean(dArr, ${CPU_TRIM_PER_SIDE});
  var madV = statsMad(dArr);
  var sdV = statsStddev(dArr, meanV);
  var cvV = meanV > 0 ? sdV / meanV : 0;

  self.postMessage({
    kind: "cpu",
    ops: targetIters,
    durations: durations,
    calibration: { opsPerMs: opsPerMs, samples: calSamples },
    warmupMs: warmupActual,
    calibrationMs: calElapsed,
    durationMs: total,
    timerResolutionMs: timerResolutionMs,
    runShape: useCoarseShape ? "coarse" : "fine",
    stats: {
      median: medianV,
      mean: meanV,
      trimmedMean: trimmedV,
      mad: madV,
      stddev: sdV,
      cv: cvV
    },
    sink: sink,
    worker: true,
    wasm: isWasm,
    crossOriginIsolated: typeof self.crossOriginIsolated !== "undefined" ? self.crossOriginIsolated : null,
    webWorker: webWorkerNav,
    webWorkerMs: webWorkerMs
  });
  };

  var finishNav = function () {
    webWorkerMs = performance.now() - webWorkerNavStart;
    postNav();
    runCpu();
  };

  if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
    navigator.userAgentData.getHighEntropyValues(hevKeys).then(function (hev) {
      webWorkerNav.highEntropyValues = hev;
      finishNav();
    }).catch(function () {
      finishNav();
    });
  } else {
    finishNav();
  }
};`;

// ---------------------------------------------------------------------------
// CPU: WORKER LAUNCHER
// ---------------------------------------------------------------------------

function cpuRunInWorker(warmupMs, calibrationMs, calWindowMs, measureBudgetMs) {
  return new Promise(function (resolve, reject) {
    try {
      const blob = new Blob([CPU_WORKER_SOURCE], {
        type: "application/javascript",
      });
      const url = URL.createObjectURL(blob);
      let w;
      try {
        w = new Worker(url);
      } catch (e) {
        URL.revokeObjectURL(url);
        return reject(e);
      }
      let navPayload = null;
      const failWith = function (message) {
        const err = new Error(message);
        err.navPayload = navPayload;
        reject(err);
      };
      const tid = setTimeout(function () {
        w.terminate();
        URL.revokeObjectURL(url);
        failWith("cpu worker timeout");
      }, CPU_WORKER_TIMEOUT_MS);
      w.onmessage = function (ev) {
        if (ev && ev.data && ev.data.kind === "nav") {
          navPayload = ev.data;
          return;
        }
        clearTimeout(tid);
        w.terminate();
        URL.revokeObjectURL(url);
        resolve(ev.data);
      };
      w.onerror = function (ev) {
        clearTimeout(tid);
        w.terminate();
        URL.revokeObjectURL(url);
        failWith(ev && ev.message ? ev.message : "cpu worker error");
      };
      let wasmBytes = null;
      if (typeof WebAssembly !== "undefined") {
        const bytes = getCpuWasmBytes();
        if (bytes) wasmBytes = Array.from(bytes);
      }
      w.postMessage({
        wasmBytes: wasmBytes,
        warmupMs: warmupMs,
        calibrationMs: calibrationMs,
        calWindowMs: calWindowMs,
        measureBudgetMs: measureBudgetMs,
      });
    } catch (e) {
      reject(e);
    }
  });
}

// ---------------------------------------------------------------------------
// CPU: MAIN-THREAD FALLBACK
// ---------------------------------------------------------------------------

function cpuRunOnMainThread(warmupMs, calibrationMs, calWindowMs, measureBudgetMs) {
  let cpuKernel = cpuKernelJS;
  let isWasm = false;
  if (typeof WebAssembly !== "undefined") {
    try {
      const bytes = getCpuWasmBytes();
      if (bytes) {
        const mod = new WebAssembly.Module(bytes);
        const k = new WebAssembly.Instance(mod).exports.cpuKernel;
        if (k(1) === cpuKernelJS(1)) {
          cpuKernel = k;
          isWasm = true;
        }
      }
    } catch (e) {}
  }

  const timerResolutionMs = probeTimerResolutionMs();
  const useCoarseShape = timerResolutionMs >= CPU_COARSE_TIMER_MS;
  const numRuns = useCoarseShape ? CPU_RUNS_COARSE : CPU_RUNS_FINE;
  const targetRunMs = measureBudgetMs / numRuns;

  const t0 = performance.now();
  let sink = 0;
  const wEnd = t0 + warmupMs;
  let batch = 1000,
    calls = 0;
  let calWinStart = 0,
    calWinOps = 0,
    totalOps = 0;
  while (true) {
    const now = performance.now();
    if (now >= wEnd) break;
    if (calWinStart === 0 && now >= wEnd - calWindowMs) calWinStart = now;
    sink = (sink + cpuKernel(batch)) | 0;
    totalOps += batch;
    if (calWinStart > 0) calWinOps += batch;
    calls++;
    if (calls === 10) batch = 5000;
    if (calls === 30) batch = 10000;
    if (calls === 100) batch = 50000;
  }
  const warmupActual = performance.now() - t0;
  const calWinElapsed =
    calWinStart > 0 ? performance.now() - calWinStart : 0;
  let roughOpsPerMs;
  if (calWinElapsed > 0) roughOpsPerMs = calWinOps / calWinElapsed;
  else if (warmupActual > 0) roughOpsPerMs = totalOps / warmupActual;
  else roughOpsPerMs = 100000;

  const calShotMs = calibrationMs / CPU_CALIBRATION_SHOTS;
  const calIters = Math.max(10000, Math.round(roughOpsPerMs * calShotMs));
  const calSamples = new Float64Array(CPU_CALIBRATION_SHOTS);
  for (let s = 0; s < CPU_CALIBRATION_SHOTS; s++) {
    const cs = performance.now();
    sink = (sink + cpuKernel(calIters)) | 0;
    calSamples[s] = performance.now() - cs;
  }
  const calElapsed = statsMedian(calSamples);
  let opsPerMs = calElapsed > 0 ? calIters / calElapsed : roughOpsPerMs;
  if (!isFinite(opsPerMs) || opsPerMs <= 0) opsPerMs = roughOpsPerMs;

  let targetIters = Math.round(opsPerMs * targetRunMs);
  if (!isFinite(targetIters) || targetIters < 1000) targetIters = 1000;
  const durations = new Float64Array(numRuns);
  for (let i = 0; i < numRuns; i++) {
    const rs = performance.now();
    const rv = cpuKernel(targetIters);
    const re = performance.now();
    sink = (sink + rv) | 0;
    durations[i] = re - rs;
  }
  const total = performance.now() - t0;

  const dArr = new Array(numRuns);
  for (let di = 0; di < numRuns; di++) dArr[di] = durations[di];
  const meanV = statsMean(dArr);
  const medianV = statsMedian(dArr);
  const trimmedV = statsTrimmedMean(dArr, CPU_TRIM_PER_SIDE);
  const madV = statsMad(dArr);
  const sdV = statsStddev(dArr, meanV);
  const cvV = meanV > 0 ? sdV / meanV : 0;

  return {
    ops: targetIters,
    durations: durations,
    calibration: { opsPerMs: opsPerMs, samples: calSamples },
    warmupMs: warmupActual,
    calibrationMs: calElapsed,
    durationMs: total,
    timerResolutionMs: timerResolutionMs,
    runShape: useCoarseShape ? "coarse" : "fine",
    stats: {
      median: medianV,
      mean: meanV,
      trimmedMean: trimmedV,
      mad: madV,
      stddev: sdV,
      cv: cvV,
    },
    sink: sink,
    worker: false,
    wasm: isWasm,
    crossOriginIsolated:
      typeof self !== "undefined" &&
      typeof self.crossOriginIsolated !== "undefined"
        ? self.crossOriginIsolated
        : null,
  };
}

// ---------------------------------------------------------------------------
// CPU: FORMAT + RUN
// ---------------------------------------------------------------------------

function _roundStats(stats) {
  if (!stats) return undefined;
  return {
    median: +stats.median.toFixed(2),
    mean: +stats.mean.toFixed(2),
    trimmedMean: +stats.trimmedMean.toFixed(2),
    mad: +stats.mad.toFixed(3),
    stddev: +stats.stddev.toFixed(3),
    cv: +stats.cv.toFixed(4),
  };
}

function formatCpuResult(raw) {
  const durations =
    raw.durations instanceof Float64Array
      ? raw.durations
      : new Float64Array(raw.durations || []);
  const calSamples =
    raw.calibration && raw.calibration.samples
      ? Array.from(raw.calibration.samples, function (d) { return +d.toFixed(2); })
      : undefined;
  return {
    runs: Array.from(durations, function (d) {
      return { ops: raw.ops, durationMs: +(d.toFixed(2)) };
    }),
    warmupMs: +(raw.warmupMs.toFixed(1)),
    calibrationMs: +(raw.calibrationMs.toFixed(2)),
    calibration: {
      opsPerMs: Math.round(raw.calibration.opsPerMs),
      samplesMs: calSamples,
    },
    durationMs: +(raw.durationMs.toFixed(1)),
    timerResolutionMs:
      typeof raw.timerResolutionMs === "number"
        ? +raw.timerResolutionMs.toFixed(4)
        : null,
    runShape: raw.runShape || null,
    stats: _roundStats(raw.stats),
    sink: raw.sink,
    worker: raw.worker,
    wasm: raw.wasm,
    crossOriginIsolated: raw.crossOriginIsolated,
  };
}

function extractWebWorkerPayload(raw) {
  if (!raw || !raw.worker || !raw.webWorker) {
    return { webWorker: null, webWorkerMs: null };
  }
  return {
    webWorker: {
      userAgent: raw.webWorker.userAgent ?? null,
      appVersion: raw.webWorker.appVersion ?? null,
      platform: raw.webWorker.platform ?? null,
      highEntropyValues: raw.webWorker.highEntropyValues ?? null,
    },
    webWorkerMs:
      typeof raw.webWorkerMs === "number" ? +raw.webWorkerMs.toFixed(2) : null,
  };
}

export async function runCpuBenchmark(opts) {
  const allowMainThread = !(opts && opts.allowMainThread === false);
  const allowWorker = !(opts && opts.allowWorker === false);
  const measureBudgetMs = CPU_BUDGET_MS - CPU_WARMUP_MS - CPU_CALIBRATION_MS;
  let workerNav = opts && opts.webWorker ? { webWorker: opts.webWorker, webWorkerMs: opts.webWorkerMs ?? null } : null;
  if (
    allowWorker &&
    typeof Worker !== "undefined" &&
    typeof Blob !== "undefined" &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function"
  ) {
    try {
      const raw = await cpuRunInWorker(
        CPU_WARMUP_MS,
        CPU_CALIBRATION_MS,
        CPU_CAL_WINDOW_MS,
        measureBudgetMs
      );
      return {
        cpu: formatCpuResult(raw),
        ...extractWebWorkerPayload(raw),
      };
    } catch (err) {
      /* Worker unavailable (CSP / disabled), errored, or the benchmark ran out of time. Fall through
         to the main-thread benchmark, but keep the static navigator data if it already arrived. */
      workerNav = err && err.navPayload ? err.navPayload : workerNav;
    }
  }
  if (!allowMainThread) {
    return {
      cpu: null,
      webWorker: workerNav ? workerNav.webWorker : null,
      webWorkerMs: workerNav ? workerNav.webWorkerMs : null,
      workerUnavailable: true,
    };
  }
  await Promise.resolve();
  return {
    cpu: formatCpuResult(
      cpuRunOnMainThread(
        CPU_WARMUP_MS,
        CPU_CALIBRATION_MS,
        CPU_CAL_WINDOW_MS,
        measureBudgetMs
      )
    ),
    ...extractWebWorkerPayload(
      workerNav
        ? {
            worker: true,
            webWorker: workerNav.webWorker,
            webWorkerMs: workerNav.webWorkerMs,
          }
        : null
    ),
  };
}

const WORKER_NAV_TIMEOUT_MS = 1000;

const WORKER_NAV_PROBE_SOURCE = `"use strict";
${WORKER_NAV_SOURCE}
var nav = readWorkerNav();
var post = function (done) {
  try { self.postMessage({ done: done, nav: nav }); } catch (x) {}
};
var finish = function () {
  post(true);
};
post(false);
try {
  if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
    navigator.userAgentData.getHighEntropyValues(hevKeys).then(function (hev) {
      nav.highEntropyValues = hev;
      finish();
    }, finish);
  } else {
    finish();
  }
} catch (x) {
  finish();
}`;

export function collectWorkerNavigator() {
  return new Promise(function (resolve) {
    if (
      typeof Worker === 'undefined' ||
      typeof Blob === 'undefined' ||
      typeof URL === 'undefined' ||
      typeof URL.createObjectURL !== 'function'
    ) {
      resolve(null);
      return;
    }
    let url = null;
    let w = null;
    let tid = null;
    let syncRead = null;
    const shape = function (nav) {
      return extractWebWorkerPayload({ worker: true, webWorker: nav }).webWorker;
    };
    const finish = function (value) {
      if (tid !== null) clearTimeout(tid);
      tid = null;
      if (w) {
        try {
          w.terminate();
        } catch (e) {}
        w = null;
      }
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
        url = null;
      }
      resolve(value);
    };
    const finishWithSyncRead = function () {
      finish(syncRead ? shape({ ...syncRead, highEntropyValues: null }) : null);
    };
    try {
      url = URL.createObjectURL(new Blob([WORKER_NAV_PROBE_SOURCE], { type: 'application/javascript' }));
      w = new Worker(url);
      w.onmessage = function (ev) {
        const msg = ev ? ev.data : null;
        if (!msg || !msg.nav) return;
        if (msg.done) finish(shape(msg.nav));
        else syncRead = msg.nav;
      };
      w.onerror = finishWithSyncRead;
      tid = setTimeout(finishWithSyncRead, WORKER_NAV_TIMEOUT_MS);
    } catch (e) {
      finish(null);
    }
  });
}

// ---------------------------------------------------------------------------
// GPU BENCHMARKS — WebGL shader benchmark.
//
// Uses a compute-heavy fragment shader (dependent sin/fract chain) on a
// 512×512 canvas so the GPU does real work.
//
// Primary path (WebGL2 + EXT_disjoint_timer_query_webgl2): queue all
// measurement queries on the GPU, drain the pipeline with a single
// readPixels(), then read each query's TIME_ELAPSED_EXT for true GPU
// execution time in nanoseconds. GPU_DISJOINT_EXT lets us detect
// preemption / context switches and discard+retry that batch.
//
// Fallback path: per-run readPixels(1,1) sync timing (the original
// implementation).
// ---------------------------------------------------------------------------

const GPU_FRAG_SOURCE =
  "precision highp float;" +
  "uniform float u_seed;" +
  "void main(){" +
  "vec2 uv=gl_FragCoord.xy/" + GPU_CANVAS_SIZE + ".0;" +
  "float v=u_seed;" +
  "for(int i=0;i<" + GPU_SHADER_ITERS + ";i++){" +
  "v=fract(sin(dot(uv+v,vec2(12.9898,78.233)))*43758.5453);" +
  "}" +
  "gl_FragColor=vec4(v,v,v,1.0);" +
  "}";

function gpuWebGLSetup(gl) {
  if (!gl) return null;
  try {
    const vs = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(
      vs,
      "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}"
    );
    gl.compileShader(vs);
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) return null;

    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fs, GPU_FRAG_SOURCE);
    gl.compileShader(fs);
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      gl.deleteShader(vs);
      return null;
    }

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteProgram(program);
      return null;
    }
    gl.useProgram(program);

    const seedLoc = gl.getUniformLocation(program, "u_seed");

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const loc = gl.getAttribLocation(program, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    return { program, buf, vs, fs, loc, seedLoc };
  } catch (e) {
    return null;
  }
}

function gpuIssueDraws(gl, setup, numDraws, seed) {
  for (let i = 0; i < numDraws; i++) {
    gl.uniform1f(setup.seedLoc, seed + i * 0.001);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

function gpuDrainPipeline(gl) {
  try {
    const pixel = new Uint8Array(4);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    return pixel[0];
  } catch (e) {
    // Blocked in Tor / Firefox resistFingerprinting even when WebGL draws work.
    console.log(e);
    return null;
  }
}

// readPixels variant: synchronous wall-clock timing including driver sync.
function gpuDrawAndSync(gl, setup, numDraws, seed) {
  gpuIssueDraws(gl, setup, numDraws, seed);
  return gpuDrainPipeline(gl);
}

function gpuWebGLCleanup(gl, setup) {
  try {
    if (setup.buf) gl.deleteBuffer(setup.buf);
    if (setup.program) gl.deleteProgram(setup.program);
    if (setup.vs) gl.deleteShader(setup.vs);
    if (setup.fs) gl.deleteShader(setup.fs);
  } catch (e) {}
}

function _yield(ms) {
  return new Promise(function (r) { setTimeout(r, ms); });
}

// Issue a batch of `numRuns` timer-elapsed queries back-to-back, drain
// the GPU pipeline, then read each query's nanosecond duration.
// Returns null on disjoint event so the caller can retry.
async function gpuMeasureBatchTimerQuery(
  gl,
  ext,
  setup,
  drawsPerRun,
  numRuns,
  seedBase
) {
  const queries = new Array(numRuns);
  for (let i = 0; i < numRuns; i++) {
    const q = gl.createQuery();
    gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
    gpuIssueDraws(gl, setup, drawsPerRun, seedBase + i);
    gl.endQuery(ext.TIME_ELAPSED_EXT);
    queries[i] = q;
  }
  const sink = gpuDrainPipeline(gl);
  if (sink === null) {
    for (let i = 0; i < numRuns; i++) gl.deleteQuery(queries[i]);
    return null;
  }

  const lastQ = queries[numRuns - 1];
  const pollStart = performance.now();
  let ready = false;
  while (performance.now() - pollStart < GPU_QUERY_POLL_TIMEOUT_MS) {
    if (gl.getQueryParameter(lastQ, gl.QUERY_RESULT_AVAILABLE)) {
      ready = true;
      break;
    }
    await _yield(1);
  }

  const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) === true;
  const durations = new Float64Array(numRuns);
  let unavailable = 0;
  for (let i = 0; i < numRuns; i++) {
    if (gl.getQueryParameter(queries[i], gl.QUERY_RESULT_AVAILABLE)) {
      durations[i] = gl.getQueryParameter(queries[i], gl.QUERY_RESULT) / 1e6;
    } else {
      durations[i] = NaN;
      unavailable++;
    }
    gl.deleteQuery(queries[i]);
  }

  if (disjoint || !ready || unavailable === numRuns) return null;
  return { durations, sink, partial: unavailable > 0, unavailable };
}

// readPixels-based batch (synchronous per-run wall-clock).
function gpuMeasureBatchReadPixels(gl, setup, drawsPerRun, numRuns, seedBase) {
  const durations = new Float64Array(numRuns);
  let sink = 0;
  for (let i = 0; i < numRuns; i++) {
    const rs = performance.now();
    const s = gpuDrawAndSync(gl, setup, drawsPerRun, seedBase + i);
    if (s === null) return null;
    sink = (sink + s) | 0;
    durations[i] = performance.now() - rs;
  }
  return { durations, sink };
}

function gpuTestCanvas2D(budget) {
  const result = { ops: 0, available: false, method: "canvas2d" };
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext("2d");
    if (!ctx) return result;
    let ops = 0;
    const end = performance.now() + budget;
    while (performance.now() < end) {
      ctx.fillStyle = ops % 2 === 0 ? "#ff0000" : "#0000ff";
      ctx.fillRect(0, 0, 256, 256);
      const grad = ctx.createLinearGradient(0, 0, 256, 256);
      grad.addColorStop(0, "#000");
      grad.addColorStop(1, "#fff");
      ctx.fillStyle = grad;
      ctx.globalCompositeOperation = "multiply";
      ctx.fillRect(0, 0, 256, 256);
      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath();
      ctx.arc(128, 128, 100, 0, Math.PI * 2);
      ctx.stroke();
      ops++;
    }
    result.ops = ops;
    result.available = true;
  } catch (e) {}
  return result;
}

function gpuFallbackCanvas2D(t0, contextInitMs, setupMs) {
  const c2d = gpuTestCanvas2D(GPU_BUDGET_MS);
  return {
    method: c2d.available ? "canvas2d" : null,
    runs: c2d.available ? [{ ops: c2d.ops }] : [],
    contextInitMs: contextInitMs,
    setupMs: setupMs,
    durationMs: +(performance.now() - t0).toFixed(1),
  };
}

// ---------------------------------------------------------------------------
// GPU RUNNER — warm-up → calibrate → fixed-iteration measure
// ---------------------------------------------------------------------------

export async function runGpuBenchmark() {
  const t0 = performance.now();
  const gl = getGLContext();
  const contextInitMs = +(performance.now() - t0).toFixed(1);

  if (!gl) {
    const c2d = gpuTestCanvas2D(GPU_BUDGET_MS);
    return {
      method: c2d.available ? "canvas2d" : null,
      runs: c2d.available ? [{ ops: c2d.ops }] : [],
      contextInitMs: contextInitMs,
      durationMs: +(performance.now() - t0).toFixed(1),
    };
  }

  const tSetup = performance.now();
  const setup = gpuWebGLSetup(gl);
  const setupMs = +(performance.now() - tSetup).toFixed(1);

  if (!setup) {
    const c2d = gpuTestCanvas2D(GPU_BUDGET_MS);
    return {
      method: c2d.available ? "canvas2d" : null,
      runs: c2d.available ? [{ ops: c2d.ops }] : [],
      contextInitMs: contextInitMs,
      setupMs: setupMs,
      durationMs: +(performance.now() - t0).toFixed(1),
    };
  }

  const timerExt = getGpuTimerQueryExt(gl);
  let sink = 0;

  // Force shader compilation with a single draw + pipeline flush.
  // First draw can be very slow (10-50 ms) due to lazy compilation.
  // readPixels sync is blocked in Tor / resistFingerprinting — fall back early.
  const compileStart = performance.now();
  const compileSink = gpuDrawAndSync(gl, setup, 1, 0.5);
  const compileMs = +(performance.now() - compileStart).toFixed(1);
  if (compileSink === null) {
    gpuWebGLCleanup(gl, setup);
    return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
  }
  sink = (sink + compileSink) | 0;

  try {
  // --- Warm-up (shader already compiled). Wall-clock based irrespective
  // of measurement primitive: warm-up is about driver/clock state, not
  // about precise timing. ---
  const warmStart = performance.now();
  const wEnd = warmStart + GPU_WARMUP_MS;
  let batch = 10, calls = 0;
  let calWinStart = 0, calWinDraws = 0, totalDraws = 0;

  while (true) {
    const now = performance.now();
    if (now >= wEnd) break;
    if (calWinStart === 0 && now >= wEnd - GPU_CAL_WINDOW_MS) calWinStart = now;
    const s = gpuDrawAndSync(gl, setup, batch, now * 0.001);
    if (s === null) {
      gpuWebGLCleanup(gl, setup);
      return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
    }
    sink = (sink + s) | 0;
    totalDraws += batch;
    if (calWinStart > 0) calWinDraws += batch;
    calls++;
    if (calls === 3) batch = 50;
    if (calls === 6) batch = 200;
    if (calls === 12) batch = 400;
  }

  const warmupActual = performance.now() - warmStart;
  const calWinElapsed = calWinStart > 0 ? performance.now() - calWinStart : 0;
  let roughDrawsPerMs;
  if (calWinElapsed > 0) roughDrawsPerMs = calWinDraws / calWinElapsed;
  else if (warmupActual > 0) roughDrawsPerMs = totalDraws / warmupActual;
  else roughDrawsPerMs = 100;

  // --- Calibration: 3 shots, median ---
  // We always calibrate with the *same primitive* we'll measure with
  // (timer-query vs. readPixels) so the resulting drawsPerMs estimate
  // is denominated in the right "currency".
  const calShotMs = GPU_CALIBRATION_MS / GPU_CALIBRATION_SHOTS;
  const calDraws = Math.max(
    10,
    Math.round(roughDrawsPerMs * calShotMs)
  );

  let calSamplesMs = new Float64Array(GPU_CALIBRATION_SHOTS);
  let calDisjoint = 0;
  let usedTimerQuery = false;

  if (timerExt) {
    const r = await gpuMeasureBatchTimerQuery(
      gl,
      timerExt,
      setup,
      calDraws,
      GPU_CALIBRATION_SHOTS,
      777
    );
    if (r && !r.partial) {
      calSamplesMs = r.durations;
      sink = (sink + r.sink) | 0;
      usedTimerQuery = true;
    } else {
      calDisjoint = 1;
    }
  }
  if (!usedTimerQuery) {
    const r = gpuMeasureBatchReadPixels(gl, setup, calDraws, GPU_CALIBRATION_SHOTS, 777);
    if (!r) {
      gpuWebGLCleanup(gl, setup);
      return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
    }
    calSamplesMs = r.durations;
    sink = (sink + r.sink) | 0;
  }

  const calElapsed = statsMedian(calSamplesMs);
  let drawsPerMs = calElapsed > 0 ? calDraws / calElapsed : roughDrawsPerMs;
  if (!isFinite(drawsPerMs) || drawsPerMs <= 0) drawsPerMs = roughDrawsPerMs;

  // --- Measurement ---
  const measureBudget = GPU_BUDGET_MS - GPU_WARMUP_MS - GPU_CALIBRATION_MS;
  const targetRunMs = measureBudget / GPU_RUNS;
  let targetDraws = Math.round(drawsPerMs * targetRunMs);
  if (!isFinite(targetDraws) || targetDraws < 1) targetDraws = 1;

  let durations;
  let method;
  let disjointRetries = 0;

  if (timerExt && usedTimerQuery) {
    let attempt = 0;
    let result = null;
    while (attempt <= GPU_DISJOINT_MAX_RETRIES) {
      result = await gpuMeasureBatchTimerQuery(
        gl,
        timerExt,
        setup,
        targetDraws,
        GPU_RUNS,
        1000 + attempt * 5000
      );
      if (result && !result.partial) break;
      attempt++;
    }
    if (result && !result.partial) {
      durations = result.durations;
      sink = (sink + result.sink) | 0;
      method = "webgl_timer_query";
      disjointRetries = attempt;
    } else {
      // All timer-query attempts hit a disjoint event — fall back.
      const fb = gpuMeasureBatchReadPixels(
        gl,
        setup,
        targetDraws,
        GPU_RUNS,
        1000
      );
      if (!fb) {
        gpuWebGLCleanup(gl, setup);
        return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
      }
      durations = fb.durations;
      sink = (sink + fb.sink) | 0;
      method = "webgl";
      disjointRetries = attempt;
    }
  } else {
    const fb = gpuMeasureBatchReadPixels(
      gl,
      setup,
      targetDraws,
      GPU_RUNS,
      1000
    );
    if (!fb) {
      gpuWebGLCleanup(gl, setup);
      return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
    }
    durations = fb.durations;
    sink = (sink + fb.sink) | 0;
    method = "webgl";
  }

  // --- Aggregate stats ---
  const dArr = new Array(durations.length);
  for (let i = 0; i < durations.length; i++) dArr[i] = durations[i];
  const meanV = statsMean(dArr);
  const medianV = statsMedian(dArr);
  const trimmedV = statsTrimmedMean(dArr, GPU_TRIM_PER_SIDE);
  const madV = statsMad(dArr);
  const sdV = statsStddev(dArr, meanV);
  const cvV = meanV > 0 ? sdV / meanV : 0;

  const total = performance.now() - t0;
  gpuWebGLCleanup(gl, setup);

  return {
    method: method,
    canvasSize: GPU_CANVAS_SIZE,
    shaderComplexity: GPU_SHADER_ITERS,
    runs: Array.from(durations, function (d) {
      return {
        draws: targetDraws,
        durationMs: isFinite(d) ? +d.toFixed(3) : null,
      };
    }),
    warmupMs: +(warmupActual.toFixed(1)),
    compileMs: compileMs,
    contextInitMs: contextInitMs,
    setupMs: setupMs,
    calibrationMs: +(calElapsed.toFixed(2)),
    calibration: {
      drawsPerMs: Math.round(drawsPerMs),
      samplesMs: Array.from(calSamplesMs, function (d) {
        return isFinite(d) ? +d.toFixed(2) : null;
      }),
    },
    durationMs: +(total.toFixed(1)),
    stats: _roundStats({
      median: medianV,
      mean: meanV,
      trimmedMean: trimmedV,
      mad: madV,
      stddev: sdV,
      cv: cvV,
    }),
    disjointRetries: disjointRetries,
    sink: sink,
  };
  } catch (e) {
    gpuWebGLCleanup(gl, setup);
    return gpuFallbackCanvas2D(t0, contextInitMs, setupMs);
  }
}

// ---------------------------------------------------------------------------
// MAIN: CPU (worker) and GPU (main thread) run in parallel
// ---------------------------------------------------------------------------

function settle(promise) {
  return Promise.resolve(promise).then(
    function (value) { return { status: "fulfilled", value: value }; },
    function (reason) { return { status: "rejected", reason: reason }; }
  );
}

export default async function getPerformanceBenchmarks(opts) {
  const cpuPromise = opts && opts.cpu ? opts.cpu : runCpuBenchmark();
  const gpuEnabled = !(opts && opts.gpu === false);
  const gpuPromise = gpuEnabled ? Promise.resolve().then(() => runGpuBenchmark()) : Promise.resolve(null);
  const [cpuSettled, gpuSettled] = await Promise.all([settle(cpuPromise), settle(gpuPromise)]);
  const cpuResult = cpuSettled.status === 'fulfilled' && cpuSettled.value ? cpuSettled.value : null;
  const errors = {};
  if (cpuSettled.status === 'rejected') errors.cpu = cpuSettled.reason;
  if (gpuSettled.status === 'rejected') errors.gpu = gpuSettled.reason;
  return {
    cpu: cpuResult ? cpuResult.cpu : null,
    gpu: gpuSettled.status === 'fulfilled' ? gpuSettled.value : null,
    webWorker: cpuResult ? cpuResult.webWorker : null,
    webWorkerMs: cpuResult ? cpuResult.webWorkerMs : null,
    gpuSkipped: !gpuEnabled,
    errors,
  };
}
